import React, { useEffect, useRef, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckIcon, 
  XMarkIcon, 
  CheckCircleIcon,
  Cog6ToothIcon,
  ArrowLeftIcon,
  PlusCircleIcon,
  TrophyIcon,
  AdjustmentsHorizontalIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useNotificationStore, NotificationSettings } from '../../store/notificationStore';
import { notificationService } from '../../services/notificationService';
import { useRefreshNotifications, useMarkAllNotificationsRead } from '../../hooks/useNotificationQueries';
import { notificationHiddenState } from '../../services/notificationHiddenState';
import NotificationItem from './NotificationItem';
import NotificationSkeleton from './NotificationSkeleton';

// Settings view component
interface NotificationSettingsViewProps {
  settings: NotificationSettings;
  onToggleType: (type: keyof NotificationSettings['enabledTypes']) => void;
}

const NotificationSettingsView: React.FC<NotificationSettingsViewProps> = ({
  settings,
  onToggleType,
}) => {
  const notificationTypeConfigs = [
    {
      key: 'position_opened' as const,
      icon: PlusCircleIcon,
      title: 'Position Opened',
      description: 'When new trading positions are opened',
      color: 'text-green-400',
    },
    {
      key: 'trade_closed' as const,
      icon: CheckCircleIcon,
      title: 'Trade Closed',
      description: 'When trades are completed with P&L results',
      color: 'text-blue-400',
    },
    {
      key: 'milestone' as const,
      icon: TrophyIcon,
      title: 'Milestones',
      description: 'P&L milestones like -50%, break-even, +25%',
      color: 'text-yellow-400',
    },
    {
      key: 'position_modified' as const,
      icon: AdjustmentsHorizontalIcon,
      title: 'Position Modified',
      description: 'When position size or settings change',
      color: 'text-purple-400',
    },
    {
      key: 'system' as const,
      icon: InformationCircleIcon,
      title: 'System Updates',
      description: 'Platform updates and maintenance notices',
      color: 'text-gray-400',
    },
  ];

  return (
    <div className="p-4 space-y-4">
      <div className="text-sm text-white/70 mb-4">
        Control which notification types appear in your feed. All notifications are still recorded - you can re-enable types to see them again.
      </div>
      
      {notificationTypeConfigs.map((config, index) => {
        const IconComponent = config.icon;
        const isEnabled = settings.enabledTypes[config.key];
        
        return (
          <motion.div
            key={config.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
              isEnabled 
                ? 'bg-white/10 border-white/20 hover:bg-white/15' 
                : 'bg-white/5 border-white/10 hover:bg-white/10'
            }`}
          >
            <div className="flex items-center space-x-3 flex-1">
              <div className={`p-2 rounded-lg bg-white/10 ${config.color}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium transition-colors ${
                  isEnabled ? 'text-white' : 'text-white/60'
                }`}>
                  {config.title}
                </h4>
                <p className={`text-sm transition-colors ${
                  isEnabled ? 'text-white/70' : 'text-white/50'
                }`}>
                  {config.description}
                </p>
              </div>
            </div>
            
            {/* Toggle Switch - Using Email Preferences Pattern */}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => onToggleType(config.key)}
                disabled={false}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 disabled:opacity-50"></div>
            </label>
          </motion.div>
        );
      })}
      
      {/* Summary Footer */}
      <div className="pt-4 border-t border-white/20">
        <div className="flex items-center justify-between text-sm">
          <span className="text-white/60">
            {Object.values(settings.enabledTypes).filter(Boolean).length} of {Object.keys(settings.enabledTypes).length} types enabled
          </span>
          <span className="text-white/50">
            Changes save automatically
          </span>
        </div>
      </div>
    </div>
  );
};

interface NotificationPanelProps {
  className?: string;
  isLoading?: boolean;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ className = '', isLoading = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Component state for smooth transitions
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const { 
    notifications, 
    unreadCount: localUnreadCount, 
    isVisible, 
    setVisible, 
    markAllAsRead, 
    hideAllNotifications,
    panelMode,
    setPanelMode,
    notificationSettings,
    toggleNotificationType,
    resetNotificationSettings,
    getSortedNotifications,
    // 🔥 NEW: Add loading state tracking
    isLoadingStored,
    storedNotificationsError
  } = useNotificationStore();

  // 🔥 FIXED: Remove duplicate useStoredNotifications - rely on NotificationBell's data
  // Use local unread count from store (already synced with API in NotificationBell)
  const unreadCount = localUnreadCount;
  
  const { refreshAll } = useRefreshNotifications();
  const markAllReadMutation = useMarkAllNotificationsRead();

  // Enhanced markAllAsRead that combines local and persistent operations
  const handleMarkAllAsRead = async () => {
    try {
      // First mark all as read locally for immediate UI feedback
      markAllAsRead();
      
      // Then mark as read persistently in the backend
      await markAllReadMutation.mutateAsync(7); // 7 days back
      
      // 🔥 FORCE REFRESH: Immediately refresh unread count after backend update
      setTimeout(() => {
        refreshAll();
      }, 300); // Wait 300ms for backend transaction to commit
      
      console.log('✅ [NotificationPanel] Successfully marked all notifications as read');
    } catch (error) {
      console.error('❌ [NotificationPanel] Failed to mark all notifications as read:', error);
      // Note: Local state was already updated, so UI still shows as read
      // The persistent state will be synced on next refresh
    }
  };

  // 🔥 NEW: Log when auto-read behavior occurs (for development feedback)
  useEffect(() => {
    if (isVisible && process.env.NODE_ENV === 'development') {
      console.log('🔔 [NotificationPanel] Panel opened - auto-read behavior active (industry standard UX)');
    }
    
    // 🔥 FORCE REFRESH: When panel closes, force refresh all notification data
    if (!isVisible && prevIsVisible.current) {
      console.log('🔄 [NotificationPanel] Panel closed, forcing notification data refresh');
      setTimeout(() => {
        refreshAll();
      }, 1000); // Increased from 200ms to 1000ms to allow backend sync to complete
    }
    
    prevIsVisible.current = isVisible;
  }, [isVisible, refreshAll]);

  // 🔥 NEW: Subtle user feedback when auto-read occurs
  const prevUnreadCount = useRef(unreadCount);
  const prevIsVisible = useRef(isVisible);
  
  useEffect(() => {
    // Detect if panel was just opened and unread count dropped to 0 (auto-read occurred)
    const wasJustOpened = !prevIsVisible.current && isVisible;
    const hadUnreadBefore = prevUnreadCount.current > 0;
    const hasNoUnreadNow = unreadCount === 0;
    
    if (wasJustOpened && hadUnreadBefore && hasNoUnreadNow) {
      // Show subtle feedback that auto-read occurred
      console.log(`📖 [NotificationPanel] Auto-marked ${prevUnreadCount.current} notifications as read (industry standard)`);
      
      // Optional: Could add a subtle toast here if desired
      // toast.success(`Marked ${prevUnreadCount.current} notifications as read`, { duration: 2000 });
    }
    
    prevUnreadCount.current = unreadCount;
    prevIsVisible.current = isVisible;
  }, [isVisible, unreadCount]);

  // 🔧 Debug function to show hidden state stats (only in development)
  const logHiddenStateStats = () => {
    const stats = notificationHiddenState.getStats();
    console.log('📊 [NotificationPanel] Hidden state stats:', stats);
    
    if (stats.total > 0) {
      console.log(`📊 Hidden: ${stats.hidden}, Dismissed: ${stats.dismissed}, Total: ${stats.total}`);
      if (stats.oldestEntry) {
        const age = Math.round((Date.now() - stats.oldestEntry) / (24 * 60 * 60 * 1000));
        console.log(`📊 Oldest hidden notification: ${age} days ago`);
      }
    }
  };

  // Auto cleanup hidden state periodically (only run once per session)
  useEffect(() => {
    const hasRunCleanup = sessionStorage.getItem('notification_cleanup_done');
    if (!hasRunCleanup) {
      notificationHiddenState.cleanup();
      sessionStorage.setItem('notification_cleanup_done', 'true');
      
      // In development, log stats
      if (process.env.NODE_ENV === 'development') {
        setTimeout(logHiddenStateStats, 1000);
      }
    }
  }, []);

  // Combine loading states
  const isLoadingNotifications = isLoading || isLoadingStored;

  // Use new filtered method
  const sortedNotifications = useMemo(() => getSortedNotifications(), [getSortedNotifications, notificationSettings, notifications]);

  // Smooth mode transition with animation
  const handleModeTransition = async (newMode: 'notifications' | 'settings') => {
    setIsTransitioning(true);
    
    // Small delay for smooth animation (using standard timing)
    await new Promise(resolve => setTimeout(resolve, 150));
    
    setPanelMode(newMode);
    setIsTransitioning(false);
  };

  // Smart positioning to avoid viewport edges and connect to bell button
  const getSmartPositioning = (): { position: string; arrowStyle: React.CSSProperties } => {
    if (typeof window === 'undefined') return { 
      position: 'top-full right-0 mt-2',
      arrowStyle: { right: '14px', transform: 'rotate(45deg)' }
    };
    
    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // On mobile, position panel to be fully visible in viewport, not relative to bell
      return { 
        position: 'fixed top-16 right-2 left-2',
        arrowStyle: { display: 'none' } // Hide arrow on mobile since panel is not connected to bell
      };
    }
    
    // On desktop, check viewport edges
    const viewportWidth = window.innerWidth;
    const panelWidth = 320; // Our panel width (w-80 = 320px)
    const rightEdgeSpace = viewportWidth - (panelRef.current?.getBoundingClientRect().right || viewportWidth);
    
    if (rightEdgeSpace < panelWidth + 20) {
      // Not enough space on the right, position to the left but still connected
      return { 
        position: 'top-full right-0 mt-2',
        arrowStyle: { right: '14px', transform: 'rotate(45deg)' }
      };
    }
    
    // Perfect right alignment - position panel so it aligns properly with the bell
    // Right-align panel with bell button and position arrow accordingly
    return { 
      position: 'top-full right-0 mt-2',
      arrowStyle: { right: '14px', transform: 'rotate(45deg)' }
    };
  };

  const { position, arrowStyle } = getSmartPositioning();

  // Close panel when clicking outside (but not on the notification bell)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking on the notification bell or its container
      if (target.closest('[data-notification-bell]') || target.closest('[data-notification-bell-container]')) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(target)) {
        setVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, setVisible]);

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        setVisible(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, setVisible]);

  // Handle notification click
  const handleNotificationClick = (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification) {
      // Pass current location so the service can set proper back navigation
      notificationService.handleNotificationClick(notification, navigate, location.pathname);
      setVisible(false); // Close panel after navigation
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop for mobile */}
          <motion.div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[64] md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVisible(false)}
          />
          
          {/* Notification Panel */}
          <motion.div
            ref={panelRef}
            className={`absolute ${position} w-auto md:w-80 md:max-w-[380px] bg-gray-900/95 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl z-[65] ${className}`}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ 
              duration: 0.2, 
              ease: 'easeOut',
              when: 'beforeChildren',
              staggerChildren: 0.05
            }}
          >
            {/* Connection Arrow - Only show on desktop and when not hidden */}
            {typeof window !== 'undefined' && window.innerWidth >= 768 && arrowStyle.display !== 'none' && (
              <div 
                className="absolute -top-2 w-4 h-4 bg-gray-900/95 border-l border-t border-white/20 rounded-tl-sm"
                style={arrowStyle}
              />
            )}
            {/* Panel Header */}
            <div className="p-4 border-b border-white/20">
              <div className="flex items-center justify-between">
                {panelMode === 'notifications' ? (
                  <>
                    {/* Notifications Header */}
                    <div className="flex flex-col">
                      <h3 className="text-lg font-semibold text-white">
                        Notifications
                      </h3>
                      {unreadCount > 0 && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full font-medium">
                            {unreadCount} new
                          </span>
                          <span className="text-xs text-white/50">
                            • Auto-marked as read when opened
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Notifications Header Actions */}
                    <div className="flex items-center space-x-1">
                      {/* 🔥 SMARTER: Mark All Read button - rarely needed due to auto-read, but available for manual use */}
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors"
                          title="Mark all as read (auto-read usually handles this)"
                          disabled={markAllReadMutation.isPending}
                        >
                          {markAllReadMutation.isPending ? (
                            <ArrowPathIcon className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                          ) : (
                            <CheckCircleIcon className="w-5 h-5 md:w-4 md:h-4" />
                          )}
                        </button>
                      )}
                      
                      {notifications.length > 0 && (
                        <button
                          onClick={hideAllNotifications}
                          className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-amber-400 hover:bg-amber-500/20 transition-colors touch-manipulation flex items-center justify-center"
                          title="Hide all notifications (local only)"
                          style={{ minWidth: '44px', minHeight: '44px' }}
                        >
                          <EyeSlashIcon className="w-5 h-5 md:w-4 md:h-4" />
                        </button>
                      )}
                      
                      {/* NEW: Settings Button */}
                      <button
                        onClick={() => handleModeTransition('settings')}
                        className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors touch-manipulation flex items-center justify-center"
                        title="Notification settings"
                        style={{ minWidth: '44px', minHeight: '44px' }} // Touch target size
                      >
                        <Cog6ToothIcon className="w-5 h-5 md:w-4 md:h-4" />
                      </button>
                      
                      <button
                        onClick={() => setVisible(false)}
                        className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors touch-manipulation flex items-center justify-center"
                        title="Close"
                        style={{ minWidth: '44px', minHeight: '44px' }} // Touch target size
                      >
                        <XMarkIcon className="w-5 h-5 md:w-4 md:h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Settings Header */}
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleModeTransition('notifications')}
                        className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors"
                        title="Back to notifications"
                      >
                        <ArrowLeftIcon className="w-4 h-4" />
                      </button>
                      <div className="flex flex-col">
                        <h3 className="text-lg font-semibold text-white">
                          Notification Settings
                        </h3>
                        <span className="text-sm text-white/60">
                          Choose which types to show
                        </span>
                      </div>
                    </div>
                    
                    {/* Settings Header Actions */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={resetNotificationSettings}
                        className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors text-sm touch-manipulation flex items-center justify-center"
                        title="Reset to defaults"
                        style={{ minWidth: '44px', minHeight: '44px' }} // Touch target size
                      >
                        Reset
                      </button>
                      
                      <button
                        onClick={() => setVisible(false)}
                        className="p-2 md:p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors touch-manipulation flex items-center justify-center"
                        title="Close"
                        style={{ minWidth: '44px', minHeight: '44px' }} // Touch target size
                      >
                        <XMarkIcon className="w-5 h-5 md:w-4 md:h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto overflow-x-hidden">
              <AnimatePresence mode="wait">
                {!isTransitioning && (
                  <motion.div
                    key={panelMode}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                  {panelMode === 'notifications' ? (
                    <>
                      {isLoadingNotifications ? (
                        /* Loading State */
                        <NotificationSkeleton count={3} />
                      ) : sortedNotifications.length === 0 ? (
                        /* Empty State */
                        <div className="p-8 text-center">
                          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                            <CheckIcon className="w-6 h-6 text-white/40" />
                          </div>
                          <h4 className="text-white/90 font-medium mb-2">
                            {storedNotificationsError ? 'Failed to load notifications' : 'All caught up!'}
                          </h4>
                          <p className="text-white/60 text-sm">
                            {storedNotificationsError 
                              ? 'There was an error loading your notifications. Try refreshing.'
                              : "You'll see notifications here when new trades, positions, or milestones occur."
                            }
                          </p>
                          {storedNotificationsError && (
                            <button
                              onClick={() => refreshAll()}
                              className="mt-4 px-4 py-2 bg-blue-500/20 border border-blue-400/30 text-blue-300 rounded-lg hover:bg-blue-500/30 transition-colors duration-200 flex items-center space-x-2 mx-auto"
                            >
                              <ArrowPathIcon className="w-4 h-4" />
                              <span>Retry</span>
                            </button>
                          )}
                        </div>
                      ) : (
                        /* Notification Items */
                        <div className="divide-y divide-white/20">
                          <AnimatePresence mode="popLayout">
                            {sortedNotifications.map((notification, index) => (
                              <motion.div
                                key={notification.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: 300, height: 0 }}
                                transition={{ 
                                  delay: index * 0.03, // Reduced delay for better performance
                                  duration: 0.2 
                                }}
                                layout
                              >
                                <NotificationItem
                                  notification={notification}
                                  onClick={() => handleNotificationClick(notification.id)}
                                />
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </>
                  ) : (
                    <NotificationSettingsView 
                      settings={notificationSettings}
                      onToggleType={toggleNotificationType}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            {/* Panel Footer */}
            {panelMode === 'notifications' && sortedNotifications.length > 0 && !isLoading && (
              <div className="p-3 border-t border-white/20 text-center">
                <p className="text-xs text-white/60">
                  Showing last {sortedNotifications.length} notification{sortedNotifications.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationPanel;